import { useEffect, useState } from 'react';

import type { CourseProgressData, CourseProgressFlow, Flow } from '../../types';
import { courses } from '../../services/api';
import { FlowPrerequisiteBootstrapResult } from './types';

interface FlowPrerequisiteGuardResult extends FlowPrerequisiteBootstrapResult {
  guardLoading: boolean;
}

interface UseFlowPrerequisiteGuardOptions {
  bypassPrerequisiteCheck?: boolean;
  courseId?: number | null;
  initialResult?: FlowPrerequisiteBootstrapResult | null;
}

export const resolveFlowPrerequisiteGuard = async (
  flowName: string,
  options: Omit<UseFlowPrerequisiteGuardOptions, 'initialResult'> = {},
): Promise<FlowPrerequisiteBootstrapResult> => {
  const { bypassPrerequisiteCheck = false, courseId = null } = options;

  if (bypassPrerequisiteCheck) {
    return { blockingFlow: null };
  }

  try {
    const flowMetaResponse = await courses.getFlowByName(flowName, { courseId });
    const courseData = flowMetaResponse?.data?.course;
    const resolvedCourseId = typeof courseData?.id === 'number' ? courseData.id : null;

    if (!resolvedCourseId) {
      return { blockingFlow: null };
    }

    const courseProgressResponse = await courses.getCourseProgress(resolvedCourseId);
    const progressData: CourseProgressData | null =
      (courseProgressResponse?.data as CourseProgressData | null) ?? null;
    if (progressData?.eligibility?.is_locked) {
      return { blockingFlow: null, lockedCourseId: resolvedCourseId };
    }
    const flowsField = progressData?.flows;
    const rawFlows: CourseProgressFlow[] = Array.isArray(flowsField) ? flowsField : [];

    const sortedFlows: CourseProgressFlow[] = [...rawFlows].sort((left, right) => {
      const leftOrder = typeof left?.order === 'number' ? left.order : Number.MAX_SAFE_INTEGER;
      const rightOrder = typeof right?.order === 'number' ? right.order : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });

    const currentEntry = sortedFlows.find(
      (entry) => entry?.flow_details?.name === flowName,
    );
    if (currentEntry?.completed_at) {
      return { blockingFlow: null };
    }
    const firstIncomplete = sortedFlows.find((entry) => !entry?.completed_at);

    if (!currentEntry || !firstIncomplete) {
      return { blockingFlow: null };
    }

    const firstIncompleteName = firstIncomplete.flow_details?.name;
    const hasComparableOrder =
      typeof currentEntry.order === 'number' && typeof firstIncomplete.order === 'number';
    const currentOrder = hasComparableOrder ? currentEntry.order : null;
    const firstIncompleteOrder = hasComparableOrder ? firstIncomplete.order : null;

    if (!firstIncompleteName || firstIncompleteName === flowName || !hasComparableOrder) {
      return { blockingFlow: null };
    }

    if (
      firstIncompleteOrder != null
      && currentOrder != null
      && firstIncompleteOrder <= currentOrder
      && firstIncomplete.flow_details
    ) {
      return { blockingFlow: firstIncomplete.flow_details as Flow };
    }

    return { blockingFlow: null };
  } catch (guardError) {
    const data = (guardError as { response?: { data?: { code?: string; course_id?: number } } })?.response?.data;
    if (data?.code === 'course_prerequisites_unmet' && data.course_id) {
      return { blockingFlow: null, lockedCourseId: data.course_id };
    }
    console.warn('Failed to evaluate flow prerequisites', guardError);
    return { blockingFlow: null, errorMessage: 'We could not check lesson access. Please try again.' };
  }
};

export const useFlowPrerequisiteGuard = (
  flowName: string,
  options: UseFlowPrerequisiteGuardOptions = {},
): FlowPrerequisiteGuardResult => {
  const { bypassPrerequisiteCheck = false, courseId = null, initialResult = null } = options;
  const [guardLoading, setGuardLoading] = useState<boolean>(() => !bypassPrerequisiteCheck && !initialResult);
  const [guardResult, setGuardResult] = useState<FlowPrerequisiteBootstrapResult>(initialResult ?? { blockingFlow: null });

  useEffect(() => {
    if (bypassPrerequisiteCheck) {
      setGuardResult({ blockingFlow: null });
      setGuardLoading(false);
      return;
    }

    if (initialResult) {
      setGuardResult(initialResult);
      setGuardLoading(false);
      return;
    }

    let isActive = true;

    const evaluatePrerequisites = async () => {
      setGuardLoading(true);
      setGuardResult({ blockingFlow: null });

      try {
        const result = await resolveFlowPrerequisiteGuard(flowName, {
          bypassPrerequisiteCheck,
          courseId,
        });

        if (!isActive) {
          return;
        }

        setGuardResult(result);
      } finally {
        if (isActive) {
          setGuardLoading(false);
        }
      }
    };

    evaluatePrerequisites();

    return () => {
      isActive = false;
    };
  }, [bypassPrerequisiteCheck, courseId, flowName, initialResult]);

  return { guardLoading, ...guardResult };
};
